import { makeAutoObservable, runInAction } from 'mobx';
import { PlaylistItem } from '~/services/scheduler/types';
import {
  CachedMedia,
  DownloadProgressCallback,
  IMediaManager,
  IMediaCache,
} from '~/services/media/types';
import {
  createMediaCache,
  detectStorageCapabilities,
  getCurrentCacheType,
} from '~/services/media/cacheFactory';
import downloadQueue from '~/services/media/DownloadQueue';
import { validateChecksumDetailed } from '~/services/media/checksumUtils';
import { logger } from '~/services/logging/logger';
import { detectPlatform } from '~/services/platform/utils/detectPlatform';

const LOG_PREFIX = '[MediaStore]';

const _STORAGE_THRESHOLD = 0.9; // Cleanup when 90% full
const STORAGE_TARGET = 0.85; // Target utilization 85%
const STORAGE_MAX = 0.9; // Maximum utilization 90%

/**
 * Cooldown between download retries for items that have exhausted MAX_RETRIES
 * in DownloadQueue (currently 3 attempts). The next call to `queueDownload`
 * after this window will re-enqueue the item from scratch. Keeps debug telemetry
 * intact (status='error', failedCount++) while still recovering automatically
 * once a transient failure (CORS, network outage, R2 hiccup) is resolved.
 *
 * 30s gives the underlying issue time to clear without thrashing.
 */
const ERROR_RETRY_COOLDOWN_MS = 30_000;

/**
 * Folga antes de apagar do disco um item que saiu da programação (vide syncWithSchedule).
 * Cobre o "tirou e recolocou do grupo", troca de programação de ida e volta e campanha que
 * pausa de um dia pro outro sem rebaixar nada.
 */
export const ORPHAN_GRACE_MS = 24 * 60 * 60 * 1000;

/**
 * Desde quando cada item cacheado está fora da programação ({id: epoch ms}). Persistido
 * porque o player recarrega a página periodicamente (reciclagem da TV): só em memória a
 * folga recomeçaria a cada reload e órfão nunca sairia do disco.
 */
const ORPHANED_AT_STORAGE_KEY = 'efflux_cache_orphaned_at';

function loadOrphanedAt(): Map<string, number> {
  try {
    const raw = localStorage.getItem(ORPHANED_AT_STORAGE_KEY);
    if (!raw) return new Map();
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return new Map(
      Object.entries(parsed).filter(
        (e): e is [string, number] => typeof e[1] === 'number',
      ),
    );
  } catch {
    return new Map();
  }
}

class MediaStore implements IMediaManager {
  // Observable state
  cacheStatus: Map<string, CachedMedia> = new Map();
  downloadProgress: Map<string, number> = new Map();
  isInitialized = false;

  /**
   * 2026-05-24 — Debug aid: último erro real do cache.store().
   * Em produção, prints da TV de bancada mostram "3 erros" no painel
   * mas sem mensagem — operador não tem como diagnosticar sem adb shell
   * (que Samsung bloqueia em consumer TV). Esse field é exposto no
   * overlay debug pra dar visibilidade ao erro raiz (ex: "writeBlob is
   * not a function", "QuotaExceededError", "Failed to fetch", etc).
   */
  lastCacheError: string | null = null;
  lastCacheErrorAt: number | null = null;

  /**
   * 2026-05-24 — Debug aid: rastreio de remoções do cache.
   *
   * Sintoma observado em TV Samsung consumer (UN50U8600F, Tizen 7.7) v1.0.4:
   * downloads completam, item aparece como `cached` momentaneamente, mas é
   * descartado em seguida — sem visibilidade do call site responsável.
   *
   * Hipóteses em investigação:
   *   (a) `syncWithSchedule` removendo itens por mismatch de IDs entre sync
   *       e cacheStatus
   *   (b) `cache.has()` (que chama `pathExists`) retornando false logo após
   *       write — bug API Tizen consumer
   *   (c) Integrity check no init() marcando tudo como "stale"
   *
   * Esses campos são expostos no overlay pra identificar quem está
   * removendo e quantos itens. Quando identificarmos o culpado, removemos
   * a instrumentação.
   */
  totalRemovals = 0;
  lastRemovalSource: string | null = null;
  lastRemovalAt: number | null = null;
  lastRemovalCount: number = 0;

  /**
   * 2026-05-24 — Telemetria de checksum mismatch.
   *
   * Descoberta via logs Chrome browser: o servidor está mandando checksums
   * que não correspondem ao SHA-256 dos arquivos servidos pelo R2. Tanto
   * Browser quanto Tizen calculam o mesmo hash, então o blob está íntegro
   * — é o `expected` do servidor que está errado. Hipótese: pipeline de
   * transcoding re-encoda após upload mas o checksum salvo continua sendo
   * do arquivo original (pre-transcode).
   *
   * Comportamento: a partir de v1.0.8, checksum mismatch loga warning,
   * incrementa contador, mas NÃO bloqueia o cache (graceful degradation
   * enquanto o backend não corrige). Se o env var
   * `VITE_CHECKSUM_STRICT=true` estiver setado, volta ao comportamento
   * antigo (bloqueante).
   */
  checksumMismatchCount = 0;
  lastChecksumMismatch: string | null = null;

  /**
   * v1.0.59 (2026-05-28) — Telemetria forense IDB pra diagnosticar
   * degradação de cache.store ao longo do tempo. Renato observou em
   * campo que após o primeiro crash, cache.store passa a falhar de
   * forma cumulativa (de 8 cacheados pra 0). Hipóteses sob investigação:
   *   - Quota IDB cheia (lixo de TX abortadas)
   *   - Memory leak nativo no WebView
   *   - Conexões IDB acumulando
   *
   * Sem shell remoto no Tizen Consumer 9.0, essa instrumentação é
   * a única forma de ter visibilidade do que está acontecendo dentro
   * da TV.
   */
  /** Contagem cumulativa de cache.store attempts nesta sessão */
  cacheStoreAttempts = 0;
  /** Contagem de cache.store que terminou com sucesso */
  cacheStoreSuccesses = 0;
  /** Contagem de cache.store que falhou (qualquer motivo) */
  cacheStoreFailures = 0;
  /** Duração média (ms) das cache.store que tiveram sucesso */
  cacheStoreAvgSuccessDurationMs = 0;
  /** Contagem de forceReconnect chamados nesta sessão */
  forceReconnectCount = 0;
  /** Storage estimate mais recente (atualizado a cada 30s + antes de cada store) */
  lastStorageEstimate: {
    usageMB: number;
    quotaMB: number;
    percent: number;
    timestamp: number;
  } | null = null;
  /**
   * Histórico das últimas 10 operações cache.store, mais recente
   * primeiro. Permite ver tendência (estão demorando mais? falhando
   * com mais frequência?).
   */
  recentIdbOps: Array<{
    itemName: string;
    sizeMB: number;
    durationMs: number;
    result: 'success' | 'timeout' | 'error';
    timestamp: number;
    errorMsg?: string;
  }> = [];
  /** Maximum number of operations to keep in history */
  private static readonly MAX_RECENT_OPS = 10;

  /**
   * Quota CONFIGURADA pro cache do player (em MB). Diferente de
   * navigator.storage.estimate().quota (que em Tizen Consumer 9.0
   * e Capacitor Android retorna ~80-166MB mentirosamente — task #17
   * 2026-05-28).
   *
   * O backend de cache em TV/Tablet é filesystem (vide TizenMediaCache
   * + Capacitor Filesystem), que tem acesso a GBs do disco. Mas o
   * navegador continua reportando quota baixa via IndexedDB.
   *
   * Pra digital signage, o que importa é o limite que o app se IMPÕE
   * pra não encher o disco — não o limite do navegador. Default 1GB
   * é folgado pra playlists típicas (5-10 vídeos × 30MB = 150-300MB).
   * Configurável via VITE_MAX_CACHE_MB.
   */
  get configuredCacheQuotaMB(): number {
    const fromEnv = Number(import.meta.env.VITE_MAX_CACHE_MB);
    return Number.isFinite(fromEnv) && fromEnv > 0 ? fromEnv : 1024;
  }

  /**
   * Percentual do cache configurado em uso. Usa configuredCacheQuotaMB
   * como denominador (não a quota mentirosa do navegador).
   */
  get configuredCachePercent(): number {
    const usedMB = this.totalCacheSize / (1024 * 1024);
    const quotaMB = this.configuredCacheQuotaMB;
    if (quotaMB <= 0) return 0;
    return Math.round((usedMB / quotaMB) * 100);
  }

  /**
   * Adiciona uma op IDB ao histórico (ring buffer fixo).
   */
  private trackIdbOp(op: {
    itemName: string;
    sizeMB: number;
    durationMs: number;
    result: 'success' | 'timeout' | 'error';
    errorMsg?: string;
  }): void {
    runInAction(() => {
      this.cacheStoreAttempts++;
      if (op.result === 'success') {
        this.cacheStoreSuccesses++;
        // Moving average das durações de sucesso
        const prev = this.cacheStoreAvgSuccessDurationMs;
        const n = this.cacheStoreSuccesses;
        this.cacheStoreAvgSuccessDurationMs = prev + (op.durationMs - prev) / n;
      } else {
        this.cacheStoreFailures++;
      }
      this.recentIdbOps.unshift({
        ...op,
        timestamp: Date.now(),
      });
      if (this.recentIdbOps.length > MediaStore.MAX_RECENT_OPS) {
        this.recentIdbOps.length = MediaStore.MAX_RECENT_OPS;
      }
    });
  }

  /**
   * Atualiza o storage estimate via navigator.storage.estimate.
   * Best-effort — alguns ambientes (Tizen older, simulators) não
   * suportam essa API.
   */
  private async refreshStorageEstimate(): Promise<void> {
    try {
      if (
        typeof navigator !== 'undefined' &&
        navigator.storage &&
        typeof navigator.storage.estimate === 'function'
      ) {
        const est = await navigator.storage.estimate();
        const usageMB = est.usage ? est.usage / (1024 * 1024) : 0;
        const quotaMB = est.quota ? est.quota / (1024 * 1024) : 0;
        runInAction(() => {
          this.lastStorageEstimate = {
            usageMB: Math.round(usageMB * 10) / 10,
            quotaMB: Math.round(quotaMB * 10) / 10,
            percent: quotaMB > 0 ? Math.round((usageMB / quotaMB) * 100) : 0,
            timestamp: Date.now(),
          };
        });
      }
    } catch {
      /* navigator.storage não disponível — ok */
    }
  }

  // Platform-specific cache (async initialized)
  private _mediaCache: IMediaCache | null = null;
  private _cacheInitPromise: Promise<IMediaCache> | null = null;

  /**
   * Gets the appropriate media cache for the current platform.
   * Initializes the cache lazily on first access.
   */
  private async getMediaCache(): Promise<IMediaCache> {
    if (this._mediaCache) {
      return this._mediaCache;
    }

    // Prevent multiple concurrent initializations
    if (this._cacheInitPromise) {
      return this._cacheInitPromise;
    }

    console.log(`${LOG_PREFIX} 🔧 Initializing media cache...`);

    this._cacheInitPromise = createMediaCache();
    try {
      this._mediaCache = await this._cacheInitPromise;
      console.log(
        `${LOG_PREFIX} ✅ Media cache initialized: ${getCurrentCacheType()}`,
      );
      return this._mediaCache;
    } finally {
      this._cacheInitPromise = null;
    }
  }

  /**
   * Gets the sync version of media cache (for computed properties).
   * Returns null if cache hasn't been initialized yet.
   */
  private get mediaCache(): IMediaCache | null {
    return this._mediaCache;
  }

  /**
   * Quantos blob URLs ativos o cache atual mantém. Em Tizen Consumer 9.0,
   * cada blob URL alive segura ~40 MB de memória NATIVA (não JS heap) e é
   * suspeito principal de OOM nativo. Usado pelo heartbeat do crashCapture
   * pra detectar accumulation antes do crash.
   *
   * Retorna 0 se o cache ainda não inicializou ou se ele não usa blob URLs
   * (TizenMediaCache / CapacitorFilesystemCache com file://).
   */
  get activeBlobUrlCount(): number {
    return this._mediaCache?.getActiveBlobUrlCount?.() ?? 0;
  }

  /** Vide {@link ORPHANED_AT_STORAGE_KEY}. Não observável: só a limpeza lê. */
  private orphanedAt: Map<string, number> = loadOrphanedAt();

  constructor() {
    makeAutoObservable<MediaStore, 'orphanedAt'>(this, { orphanedAt: false });
    this.setupDownloadQueue();
  }

  // ===========================================================================
  // COMPUTED PROPERTIES
  // ===========================================================================

  /**
   * Count of pending downloads.
   */
  get pendingDownloads(): number {
    let count = 0;
    this.cacheStatus.forEach((item) => {
      if (item.status === 'pending' || item.status === 'downloading') {
        count++;
      }
    });
    return count;
  }

  /**
   * All cached items.
   */
  get cachedItems(): CachedMedia[] {
    return Array.from(this.cacheStatus.values()).filter(
      (item) => item.status === 'cached',
    );
  }

  /**
   * Total cache size in bytes.
   */
  get totalCacheSize(): number {
    return this.cachedItems.reduce((sum, item) => sum + item.fileSize, 0);
  }

  /**
   * Items currently downloading.
   */
  get downloadingItems(): CachedMedia[] {
    return Array.from(this.cacheStatus.values()).filter(
      (item) => item.status === 'downloading',
    );
  }

  /**
   * Items with errors.
   */
  get errorItems(): CachedMedia[] {
    return Array.from(this.cacheStatus.values()).filter(
      (item) => item.status === 'error',
    );
  }

  /**
   * Whether there is any cached content available for offline playback.
   */
  get hasCachedContent(): boolean {
    return this.cachedItems.length > 0;
  }

  // ===========================================================================
  // INITIALIZATION
  // ===========================================================================

  /**
   * Initializes the store from existing cache.
   */
  async init(): Promise<void> {
    console.log(`${LOG_PREFIX} 🚀 Initializing...`);

    // Log storage capabilities
    const capabilities = detectStorageCapabilities();
    console.log(`${LOG_PREFIX}    └─ Platform: ${capabilities.platform}`);
    console.log(
      `${LOG_PREFIX}    └─ Secure context: ${capabilities.isSecureContext}`,
    );
    console.log(
      `${LOG_PREFIX}    └─ Storage recommendation: ${capabilities.recommendation}`,
    );

    // Initialize the cache
    const cache = await this.getMediaCache();
    const metadata = cache.getMetadata();

    runInAction(() => {
      Object.entries(metadata).forEach(([url, meta]) => {
        this.cacheStatus.set(meta.id, {
          id: meta.id,
          // Pode ser undefined em metadata salvo pré-v1.0.48 — fallback no
          // overlay continua mostrando id truncado nesses casos.
          name: meta.name,
          url,
          localUrl: '', // Will be set on demand
          downloadedAt: meta.downloadedAt,
          lastPlayedAt: meta.lastPlayedAt,
          status: 'cached',
          fileSize: meta.fileSize,
          checksum: meta.checksum,
        });
      });
      this.isInitialized = true;
    });

    const totalSize = this.formatBytes(this.totalCacheSize);
    const cacheType = getCurrentCacheType();
    console.log(
      `${LOG_PREFIX} ✅ Initialized: ${this.cachedItems.length} cached items (${totalSize})`,
    );
    console.log(`${LOG_PREFIX}    └─ Cache type: ${cacheType}`);

    // Verify cache integrity: check that blobs actually exist in Cache API
    // Persistent storage may be denied, allowing browser to evict blobs
    // while localStorage metadata survives — creating stale entries
    const staleEntries: { id: string; url: string }[] = [];
    for (const [url, meta] of Object.entries(metadata)) {
      const blobExists = await cache.has(url);
      if (!blobExists) {
        staleEntries.push({ id: meta.id, url });
      }
    }

    if (staleEntries.length > 0) {
      console.warn(
        `${LOG_PREFIX} ⚠️ Cache integrity: ${staleEntries.length}/${Object.keys(metadata).length} blobs evicted by browser`,
      );
      for (const entry of staleEntries) {
        console.warn(`${LOG_PREFIX}    └─ Evicted: ${entry.id.slice(0, 8)}...`);
        await cache.remove(entry.url); // Clean stale metadata
        runInAction(() => {
          this.cacheStatus.delete(entry.id);
        });
      }
      this._trackRemoval('init/integrity-check', staleEntries.length);
      console.log(
        `${LOG_PREFIX} 🔄 ${staleEntries.length} stale entries removed — will re-download on next sync`,
      );
    }

    // Log storage info
    const info = await this.getStorageInfo();
    if (info.quota > 0) {
      console.log(
        `${LOG_PREFIX} 📊 Storage: ${this.formatBytes(info.usage)} / ${this.formatBytes(info.quota)} (${info.percent.toFixed(1)}%)`,
      );
    }

    console.log(
      `${LOG_PREFIX} ✅ Ready: ${this.cachedItems.length} verified cached items`,
    );
  }

  // ===========================================================================
  // QUEUE MANAGEMENT
  // ===========================================================================

  /**
   * Adds an item to the download queue.
   *
   * Re-enqueue policy for errored items (cache-recovery after transient failures
   * like CORS/network outages): items in `status='error'` are eligible for
   * re-enqueue once `ERROR_RETRY_COOLDOWN_MS` has elapsed since `failedAt`.
   * This prevents tight-loop thrashing while still recovering automatically when
   * the next sync tick or playback request triggers `queueDownload`.
   */
  queueDownload(item: PlaylistItem, priority = 0): void {
    if (item.type === 'stream') return;

    // Skip if already cached
    if (this.isCached(item)) {
      return;
    }

    // Skip if already in queue
    if (downloadQueue.isInQueue(item.id)) {
      return;
    }

    const existing = this.cacheStatus.get(item.id);

    // v1.0.55 (2026-05-28) — CRITICAL FIX: skip if item is currently being
    // processed (network download in flight OR cache.store() running).
    //
    // BUG observado por Renato em campo: quando um vídeo está em
    // `cache.store()` (status='saving') por longo período (até 120s em
    // Tizen Consumer 9.0), um sync paralelo dispara `prefetchAll` que
    // chama `queueDownload` pra TODOS os itens não-cacheados, incluindo
    // o que está atualmente sendo salvo. Sem essa checagem, o item é
    // OVERWRITTEN em `cacheStatus` com status='pending' E re-enfileirado
    // em `downloadQueue.queue`. Resultado:
    //   - Original em processamento termina (timeout 30s/120s ou success)
    //   - Próximo da fila pegado: É O MESMO ITEM (cópia duplicada)
    //   - Outros itens com prioridade menor ficam atrás, NUNCA processados
    //   - Loop infinito no mesmo vídeo, fila travada
    //
    // Status 'downloading' = network fetch ativo (rápido, raro pegar
    //   prefetchAll concorrente, mas defensivo).
    // Status 'saving' = cache.store() rodando (pode levar 120s em Tizen,
    //   janela LARGA pra prefetchAll concorrente entrar).
    if (existing?.status === 'downloading' || existing?.status === 'saving') {
      return;
    }

    // If item is in 'error' state, only allow re-enqueue after cooldown.
    // Without this, a fresh sync after a recovered transient failure (e.g.,
    // CORS fix, network back up) would never retry, leaving the item stuck.
    if (existing?.status === 'error' && existing.failedAt) {
      const elapsed = Date.now() - existing.failedAt;
      if (elapsed < ERROR_RETRY_COOLDOWN_MS) {
        return;
      }
    }

    const failedCount =
      existing?.status === 'error' ? (existing.failedCount ?? 0) : 0;

    runInAction(() => {
      this.cacheStatus.set(item.id, {
        id: item.id,
        // Salva o nome no momento do enfileiramento. Garante que o overlay
        // mostra o nome correto mesmo se o item sair da playlist depois
        // (ex: sync trocou a campanha enquanto o download estava na fila).
        name: item.name,
        url: item.url,
        localUrl: '',
        downloadedAt: '',
        status: 'pending',
        fileSize: item.fileSize,
        checksum: item.checksum,
        // Preserve failedCount across retries for telemetry; cleared on successful cache.
        ...(failedCount > 0 ? { failedCount } : {}),
      });
    });

    downloadQueue.enqueue(item, priority);
  }

  /**
   * 2026-06-25 — Re-enfileira items em `status='error'` cujo cooldown
   * `ERROR_RETRY_COOLDOWN_MS` já venceu.
   *
   * Por que isto existe: o caminho normal de retry passa por `prefetchAll`,
   * que só é disparado quando o sync retorna `playlistItems`. Quando o
   * server responde `update_required='no'` (hash short-circuit — library
   * inalterada), `prefetchAll([])` é early-returnado e items errored ficam
   * permanentemente sem retry — toda exibição cai pro streaming HTTPS.
   *
   * `queueDownload` reusa todo o gate de re-entry (cooldown, isInQueue,
   * status='downloading'/'saving'). Aqui só rebuildamos um `PlaylistItem`
   * mínimo a partir de `CachedMedia` — `DownloadQueue` + `onComplete` só
   * leem id/url/name/fileSize/checksum, então campos "ricos" (type,
   * duration, mimeType, schedule, validade etc) são preenchidos com
   * valores neutros e nunca consumidos.
   */
  retryFailedDownloads(): void {
    const now = Date.now();
    let retried = 0;
    this.cacheStatus.forEach((cached) => {
      if (cached.status !== 'error' || !cached.failedAt) return;
      if (now - cached.failedAt < ERROR_RETRY_COOLDOWN_MS) return;
      if (downloadQueue.isInQueue(cached.id)) return;

      const stub = {
        id: cached.id,
        url: cached.url,
        name: cached.name ?? cached.id,
        fileSize: cached.fileSize,
        checksum: cached.checksum,
        type: 'video',
        duration: 0,
        mimeType: '',
      } as unknown as PlaylistItem;

      this.queueDownload(stub);
      retried++;
    });

    if (retried > 0) {
      console.log(
        `${LOG_PREFIX} 🔄 retryFailedDownloads: re-enfileirou ${retried} item(s) errored com cooldown vencido`,
      );
    }
  }

  /**
   * Downloads an item immediately (high priority).
   */
  async downloadNow(item: PlaylistItem): Promise<void> {
    if (this.isCached(item)) {
      return;
    }

    // Add to queue with very high priority
    this.queueDownload(item, 1000);

    // Wait for completion
    return new Promise((resolve, reject) => {
      const checkInterval = setInterval(() => {
        const status = this.cacheStatus.get(item.id);

        if (status?.status === 'cached') {
          clearInterval(checkInterval);
          resolve();
        } else if (status?.status === 'error') {
          clearInterval(checkInterval);
          reject(new Error(status.error || 'Download failed'));
        }
      }, 100);

      // Timeout after 5 minutes
      setTimeout(
        () => {
          clearInterval(checkInterval);
          reject(new Error('Download timeout'));
        },
        5 * 60 * 1000,
      );
    });
  }

  /**
   * Gets the local URL for a cached item.
   * @deprecated Use getLocalUrlAsync instead - this always returns null now
   */
  getLocalUrl(_item: PlaylistItem): string | null {
    // Blob URLs are no longer cached in the store
    // Use getLocalUrlAsync which creates a fresh blob URL each time
    return null;
  }

  /**
   * Gets the local URL asynchronously (creates blob URL or file URL).
   *
   * NOTE: For browser, creates a NEW blob URL. The caller is responsible for
   * revoking it when done (via URL.revokeObjectURL). For Tizen, returns file:// URL.
   *
   * If cache access fails, returns null (caller should fallback to remote URL).
   */
  async getLocalUrlAsync(
    item: Pick<PlaylistItem, 'id' | 'name' | 'url'>,
  ): Promise<string | null> {
    const cached = this.cacheStatus.get(item.id);
    if (!cached || cached.status !== 'cached') {
      // Estado normal durante o download — não merece atenção do operador.
      logger.debug(`${LOG_PREFIX} ${item.name} not cached yet`);
      return null;
    }

    try {
      // CRITICAL: Use the URL from cacheStatus (matches Cache API key),
      // NOT item.url from current sync.
      //
      // Quando o backend usa storage R2 (Cloudflare/S3), cada sync gera uma
      // NOVA presigned URL — mesmo objeto, mesmo host, mas query string com
      // {@code X-Amz-Signature} e {@code X-Amz-Date} novos. Isso quer dizer
      // que `item.url` (do sync atual) e `cached.url` (capturada no momento
      // do download) NUNCA batem em R2, mesmo sem nada ter mudado.
      // Usar `cached.url` é correto: é a chave que casa com o blob no
      // IndexedDB/Cache API. A diferença é totalmente esperada.
      //
      // (Anterior: o comentário aqui dizia "URLs may differ if server IP
      // changed" — engano. IP não muda; é o signature do presigned que
      // refresca toda chamada do AWS SDK presignGetObject.)
      const cacheUrl = cached.url;

      if (cacheUrl !== item.url) {
        // Distinguir 2 cenários (PLAN_FIX_PLAYER_WARNINGS, Opção A):
        //
        //   (a) Presigned refresh — mesmo origin + pathname, query diferente.
        //       Acontece a cada sync porque o AWS SDK regera signature/expires
        //       toda chamada de presignGetObject. Totalmente esperado em R2/S3.
        //       → DEBUG (silenciado em prod, capturado no LogRingBuffer).
        //
        //   (b) Mismatch real — origin ou pathname mudou. Sinal de bucket
        //       migrado, hostname trocado, objeto renomeado, etc. Pode indicar
        //       bug ou mudança de infra que precisa atenção.
        //       → WARN (continua aparecendo no DevTools).
        let isJustPresignedRefresh = false;
        try {
          const a = new URL(cacheUrl);
          const b = new URL(item.url);
          isJustPresignedRefresh =
            a.origin === b.origin && a.pathname === b.pathname;
        } catch {
          // URLs malformadas → trata como mismatch real (warn).
        }

        if (isJustPresignedRefresh) {
          logger.debug(
            `${LOG_PREFIX} 🔄 Presigned URL refreshed for ${item.name} (using cached blob)`,
          );
        } else {
          // Mismatch genuíno — mantém sinal pro operador investigar.
          logger.warn(
            `${LOG_PREFIX} URL mismatch for ${item.name} (origin/path changed — possible bucket migration)`,
          );
          logger.debug(
            `${LOG_PREFIX}    └─ Cache key: ${cacheUrl?.substring(0, 80)}`,
          );
          logger.debug(
            `${LOG_PREFIX}    └─ Sync URL:  ${item.url?.substring(0, 80)}`,
          );
        }
      }

      // Get local URL from platform-specific cache.
      // Race against a 5s timeout: on Android tablets, IndexedDB transactions
      // can stall indefinitely under memory pressure, hanging the Promise and
      // keeping isUrlLoading=true until the 10s safety timer fires — meaning
      // the <img> is never rendered. Falling back to remote after 5s is better
      // than blocking the entire playback slot.
      //
      // 2026-05-24 — IMPORTANTE cancelar o timer quando o cache resolve antes
      // dos 5s. Sem o clearTimeout, o callback do setTimeout dispara mesmo
      // depois do Promise.race já ter resolvido, e loga falso-positivo
      // "IndexedDB timed out" pra todo item cacheado (observado no browser
      // com Cache: 6 cached, Fonte: Cache offline — warns aparecendo 5s
      // depois de cada chamada). O resolve(null) tardio é descartado pelo
      // race, mas o logger.warn já executou.
      const cache = await this.getMediaCache();
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      try {
        const localUrl = await Promise.race([
          cache.getLocalUrl(cacheUrl),
          new Promise<null>((resolve) => {
            timeoutId = setTimeout(() => {
              timeoutId = undefined; // marca disparado pra não tentar clear depois
              // Sintoma real: cache local demorou 5s+ — fica como WARN.
              logger.warn(
                `${LOG_PREFIX} IndexedDB timed out for ${item.name} — falling back to remote`,
              );
              resolve(null);
            }, 5000);
          }),
        ]);

        if (!localUrl) {
          // Estado de cache inconsistente (entry de status sem blob) — sinaliza
          // pra time entender que houve eviction. Mantém WARN.
          logger.warn(
            `${LOG_PREFIX} Blob not found for ${item.name} — cleaning stale entry`,
          );
          // Blob was evicted by browser — DON'T call cache.remove() which
          // would delete the blob if it exists under a slightly different key.
          // Just clean the stale cacheStatus entry so it can be re-downloaded.
          runInAction(() => {
            this.cacheStatus.delete(item.id);
          });
        }

        return localUrl;
      } finally {
        if (timeoutId !== undefined) {
          clearTimeout(timeoutId);
        }
      }
    } catch (error) {
      logger.error(
        `${LOG_PREFIX} getLocalUrlAsync error for ${item.name}:`,
        error,
      );
      // Don't destroy cache data on transient errors — just remove status
      runInAction(() => {
        this.cacheStatus.delete(item.id);
      });
      return null;
    }
  }

  /**
   * Checks if an item is cached.
   */
  isCached(item: Pick<PlaylistItem, 'id'>): boolean {
    const cached = this.cacheStatus.get(item.id);
    return cached?.status === 'cached';
  }

  /**
   * Records a cache removal event for the debug overlay.
   *
   * Skips no-op calls (count === 0) so the overlay only updates when an
   * actual removal happened. Wrapped in runInAction because MobX observers
   * (the overlay) read these fields.
   */
  private _trackRemoval(source: string, count: number): void {
    if (count <= 0) return;
    runInAction(() => {
      this.totalRemovals += count;
      this.lastRemovalSource = source;
      this.lastRemovalCount = count;
      this.lastRemovalAt = Date.now();
    });
    console.warn(
      `${LOG_PREFIX} 🗑️ REMOVAL TRACKED: ${count} item(s) removed by [${source}]. Session total: ${this.totalRemovals}`,
    );
  }

  /**
   * Atualiza as marcas de órfão: tira quem voltou à programação, esquece quem já não está
   * no cache e marca com `now` quem acabou de sair. Devolve quantos voltaram.
   */
  private updateOrphanMarks(
    scheduledIds: Set<string>,
    cachedIds: Set<string>,
    orphans: Pick<CachedMedia, 'id'>[],
    now: number,
  ): number {
    let reused = 0;
    for (const id of Array.from(this.orphanedAt.keys())) {
      if (scheduledIds.has(id)) {
        this.orphanedAt.delete(id);
        reused++;
      } else if (!cachedIds.has(id)) {
        this.orphanedAt.delete(id);
      }
    }
    for (const item of orphans) {
      if (!this.orphanedAt.has(item.id)) this.orphanedAt.set(item.id, now);
    }
    this.persistOrphanMarks();
    return reused;
  }

  private forgetOrphan(id: string): void {
    if (this.orphanedAt.delete(id)) this.persistOrphanMarks();
  }

  private persistOrphanMarks(): void {
    try {
      localStorage.setItem(
        ORPHANED_AT_STORAGE_KEY,
        JSON.stringify(Object.fromEntries(this.orphanedAt)),
      );
    } catch {
      // Best-effort: sem storage a folga só recomeça no próximo boot.
    }
  }

  /**
   * Gets the cache status of all known items.
   */
  getCacheStatus(): CachedMedia[] {
    return Array.from(this.cacheStatus.values());
  }

  /**
   * Gets the download progress for an item (0-100).
   */
  getProgress(itemId: string): number {
    return this.downloadProgress.get(itemId) ?? 0;
  }

  // ===========================================================================
  // CLEANUP
  // ===========================================================================

  /**
   * Removes items not in the active list.
   */
  async cleanupExpired(activeItems: PlaylistItem[]): Promise<void> {
    const activeIds = new Set(activeItems.map((i) => i.id));
    const allCached = this.cachedItems;

    // Find items not in active list
    const toRemove = allCached.filter((item) => !activeIds.has(item.id));

    if (toRemove.length > 0) {
      const cache = await this.getMediaCache();
      for (const item of toRemove) {
        await cache.remove(item.url);
        runInAction(() => {
          this.cacheStatus.delete(item.id);
        });
        this.forgetOrphan(item.id);
      }

      console.log(`${LOG_PREFIX} Cleaned up ${toRemove.length} expired items`);
      this._trackRemoval('cleanupExpired', toRemove.length);
    }
  }

  /**
   * Frees up space using LRU eviction.
   */
  async cleanupLRU(targetBytes: number): Promise<number> {
    const cache = await this.getMediaCache();
    // Órfãos em folga saem primeiro: estão fora da programação, são o seguro mais barato.
    const lruUrls = cache.getUrlsSortedByLastAccess();
    const isOrphan = (url: string) => {
      const id = cache.getMetadataForUrl(url)?.id;
      return id != null && this.orphanedAt.has(id);
    };
    const sortedUrls = [
      ...lruUrls.filter(isOrphan),
      ...lruUrls.filter((url) => !isOrphan(url)),
    ];
    let freedBytes = 0;
    let removedCount = 0;

    for (const url of sortedUrls) {
      if (freedBytes >= targetBytes) break;

      const metadata = cache.getMetadataForUrl(url);
      if (!metadata) continue;

      await cache.remove(url);
      freedBytes += metadata.fileSize;
      removedCount++;

      runInAction(() => {
        this.cacheStatus.delete(metadata.id);
      });
      this.forgetOrphan(metadata.id);
    }

    if (freedBytes > 0) {
      console.log(
        `${LOG_PREFIX} LRU cleanup freed ${this.formatBytes(freedBytes)}`,
      );
      this._trackRemoval('cleanupLRU', removedCount);
    }

    return freedBytes;
  }

  /**
   * Syncs cache with current schedule. Called after each successful sync.
   *
   * <p>O que saiu da programação NÃO é apagado na hora: fica no disco por
   * {@link ORPHAN_GRACE_MS} (regra cache-offline-first: "só remover o que saiu da
   * programação, com folga"). Sair e voltar de um grupo exclusivo apagava o cache inteiro
   * e o player voltava dependendo da rede — com a folga, ao voltar, tudo já está em disco
   * e é reaproveitado. Sob pressão de espaço os órfãos são os primeiros a sair
   * ({@link cleanupLRU}).</p>
   *
   * @param scheduledItems - All items currently in the schedule
   */
  async syncWithSchedule(scheduledItems: PlaylistItem[]): Promise<void> {
    const scheduledIds = new Set(scheduledItems.map((i) => i.id));
    const allCached = this.cachedItems;
    const cachedIds = new Set(allCached.map((i) => i.id));
    const now = Date.now();

    // Calculate diff
    const orphans = allCached.filter((item) => !scheduledIds.has(item.id));
    const toKeep = allCached.filter((item) => scheduledIds.has(item.id));
    const toAdd = scheduledItems.filter((item) => !cachedIds.has(item.id));

    const reused = this.updateOrphanMarks(
      scheduledIds,
      cachedIds,
      orphans,
      now,
    );
    const toRemove = orphans.filter(
      (item) => now - (this.orphanedAt.get(item.id) ?? now) >= ORPHAN_GRACE_MS,
    );
    const inGrace = orphans.length - toRemove.length;

    // Log consolidated summary — uma linha quando nada mudou, multi-linha
    // só quando há trabalho real (remover/baixar).
    const noChange = toRemove.length === 0 && toAdd.length === 0;
    if (noChange) {
      // Sync idempotente — debug pra não poluir
      console.debug(
        `${LOG_PREFIX} 📊 Cache diff: ✓ ${toKeep.length} mantidos, ⏳ ${inGrace} em folga, nada a mudar`,
      );
    } else {
      console.info(
        `${LOG_PREFIX} 📊 Cache diff: 🗑️ ${toRemove.length} remover, ⏳ ${inGrace} em folga, ↓ ${toAdd.length} baixar, ✓ ${toKeep.length} manter`,
      );
      // Detalhes só em debug (item por item)
      toRemove.forEach((item) =>
        console.debug(`${LOG_PREFIX}    - ${item.id.slice(0, 8)}`),
      );
      toAdd.forEach((item) => console.debug(`${LOG_PREFIX}    + ${item.name}`));
    }
    if (reused > 0) {
      console.info(
        `${LOG_PREFIX} ♻️ ${reused} item(ns) voltaram à programação durante a folga — reaproveitados do disco`,
      );
    }

    if (toRemove.length === 0) {
      return;
    }

    const totalSizeToRemove = toRemove.reduce(
      (sum, item) => sum + item.fileSize,
      0,
    );
    console.log(
      `${LOG_PREFIX} 🗑️ Removing ${toRemove.length} orphaned assets (${this.formatBytes(totalSizeToRemove)})`,
    );

    // Remove from cache
    const cache = await this.getMediaCache();
    for (const item of toRemove) {
      console.log(`${LOG_PREFIX}    └─ Removing: ${item.id}`);
      await cache.remove(item.url);
      runInAction(() => {
        this.cacheStatus.delete(item.id);
      });
      this.forgetOrphan(item.id);
    }
    this._trackRemoval('syncWithSchedule', toRemove.length);

    // Also cancel any pending downloads for removed items
    const pendingToCancel = Array.from(this.cacheStatus.values())
      .filter(
        (item) =>
          (item.status === 'pending' || item.status === 'downloading') &&
          !scheduledIds.has(item.id),
      )
      .map((item) => item.id);

    if (pendingToCancel.length > 0) {
      console.log(
        `${LOG_PREFIX} ⏹️ Cancelling ${pendingToCancel.length} orphaned downloads`,
      );
      this.cancelDownloads(pendingToCancel);
    }

    console.log(
      `${LOG_PREFIX} ✅ Sync complete - cache now has ${this.cachedItems.length} items`,
    );
  }

  /**
   * Prefetches all items in the schedule.
   * Downloads everything proactively for offline resilience.
   *
   * @param items - All playlist items to prefetch
   * @param currentItemId - Optional ID of currently playing item (highest priority)
   */
  async prefetchAll(
    items: PlaylistItem[],
    currentItemId?: string,
  ): Promise<void> {
    console.debug(
      `${LOG_PREFIX} 📥 Starting prefetch for ${items.length} scheduled items`,
    );

    if (items.length === 0) {
      console.log(`${LOG_PREFIX} ⚠️ No items to prefetch`);
      return;
    }

    // v1.0.41: removido enforceStorageLimit preventivo.
    //
    // Antes: a cada prefetchAll (sync periódico), checava se
    // navigator.storage.estimate().quota * 0.9 < usage atual, e se sim,
    // disparava cleanupLRU pra trazer pra 85%. Resultado em Tizen
    // Consumer 9.0: navigator retornava quota=80MB (limitação Tizen
    // mesmo com unlimitedstorage privilege, task #51), então aos ~72MB
    // de cache disparava LRU e removia vídeos PERFEITAMENTE bons.
    //
    // Nova política (v1.0.41+): tentar baixar TUDO. Só fazer LRU quando
    // IndexedDB rejeitar com QuotaExceededError de verdade (lógica
    // existente em IndexedDBCache.store linhas 309-358). Se mesmo após
    // cleanup o store falhar, o item segue tocando via HTTPS streaming
    // (fallback nativo do <video> que já funciona).
    //
    // Tradeoff: cache pode encher mais que 90% do limite OS, mas isso
    // é o que o Renato quer pra digital signage (offline resilience >
    // teoria de quota).

    // v1.0.53 (2026-05-28) — Política de priorização Tizen-específica.
    //
    // CONTEXTO: Renato observou em campo que o PRIMEIRO vídeo da playlist
    // sempre falhava no cache em Tizen Consumer TV (originalmente Chanel,
    // depois Prada após deletar Chanel, depois quem fosse o primeiro).
    // Erro: "IndexedDB transaction timeout" mesmo com watchdog generoso.
    //
    // CAUSA RAIZ: o currentMedia sempre era priorizado no topo da fila,
    // então o `cache.store()` dele rodava ENQUANTO ele mesmo estava
    // tocando via HTTP stream (porque ainda não estava cacheado). Em
    // Tizen Consumer 9.0, essa contenção entre decoder + HTTP fetch +
    // IDB write do MESMO conteúdo travava a WebView native.
    //
    // v1.0.53 (originalmente): EXCLUIA o currentMedia da fila. Funcionou
    // pros outros vídeos cachearem, mas Renato apontou bug colateral em
    // v1.0.56: o primeiro vídeo do boot NUNCA mais cacheava, mesmo
    // depois de deixar de ser current. Razão: prefetchAll só é chamado
    // quando sync detecta mudança de conteúdo. Se nada muda, current
    // antigo nunca volta pra fila.
    //
    // v1.0.57 (HOJE): em vez de EXCLUIR, coloca currentMedia no FINAL
    // da fila. Garante que:
    //   - Outros vídeos cacheiam primeiro (objetivo original mantido)
    //   - currentMedia eventualmente entra na fila normal
    //   - Quando chegar a vez dele, current provavelmente já é outro
    //     (loop avançou) → cacheia sem conflito
    //   - Se ainda for current na sua vez, as protections v1.0.55
    //     (no-duplicate-enqueue) + v1.0.56 (no blob URL revoke) +
    //     v1.0.54 (forceReconnect) cobrem
    //
    // Browser/Android: mantém prioridade do current.
    const isTizen = detectPlatform() === 'tizen';

    let sortedItems: PlaylistItem[];
    if (isTizen && currentItemId) {
      // Tizen: current vai pro FINAL da fila (não exclui).
      const nonCurrent = items.filter((item) => item.id !== currentItemId);
      const current = items.find((item) => item.id === currentItemId);
      sortedItems = current ? [...nonCurrent, current] : nonCurrent;
      console.log(
        `${LOG_PREFIX} 🎯 Tizen prefetch — currentMedia (${currentItemId.slice(0, 8)}...) movido pro final da fila`,
      );
    } else {
      // Outras plataformas: comportamento original (current priorizado).
      sortedItems = [...items].sort((a, b) => {
        if (a.id === currentItemId) return -1;
        if (b.id === currentItemId) return 1;
        return 0;
      });
    }

    // R5 — a emergencial baixa POR ÚLTIMO (2026-09-24).
    //
    // O servidor anexa a campanha de emergência a TODO player 24/7, e ela entra
    // no `files[]` como qualquer outra — logo, na fila de download. Só que o
    // scheduler a mantém fora do loop: é conteúdo que o player provavelmente
    // NUNCA vai exibir. Como a prioridade aqui é posicional
    // (`sortedItems.length - index`) e a fila roda com 1-2 downloads
    // simultâneos, um fallback pesado ocupava metade da banda enquanto a imagem
    // que entra na tela em 10s ainda não tinha chegado. O item não-cacheado é
    // pulado pelo Stream ("advancing silently during cache warm-up") → a tela
    // pisca trocando de conteúdo sem parar.
    //
    // Caso real (2026-09-23): emergencial com 33MB + 28MB = 81% dos 75MB que
    // cada player precisava baixar, pra 14MB de conteúdo que ele de fato tocava.
    //
    // NÃO é lazy-load: o download continua acontecendo, só que na banda ociosa,
    // depois do loop. Isso preserva o mandato offline-first — quando a loja ficar
    // sem internet e a campanha expirar, o fallback já está em disco. Se ainda
    // assim ele for acionado sem estar cacheado, o player toca por streaming
    // remoto (`usePlayableUrl` resolve REMOTE no cache miss) e o download é
    // promovido normalmente.
    const fallbackItems = sortedItems.filter((i) => i.isEmergencyFallback);
    if (fallbackItems.length > 0) {
      sortedItems = [
        ...sortedItems.filter((i) => !i.isEmergencyFallback),
        ...fallbackItems,
      ];
      console.log(
        `${LOG_PREFIX} 🛟 ${fallbackItems.length} item(ns) da emergencial movidos pro fim da fila (baixam depois do loop)`,
      );
    }

    // Calculate total size to download
    const notCached = sortedItems.filter((item) => !this.isCached(item));
    const alreadyCached = items.length - notCached.length;
    const totalSize = notCached.reduce((sum, item) => sum + item.fileSize, 0);

    if (notCached.length === 0) {
      console.debug(
        `${LOG_PREFIX} ✅ All ${items.length} items already cached - ready for offline`,
      );
      return;
    }

    console.log(`${LOG_PREFIX} 📊 Prefetch status:`);
    console.log(
      `${LOG_PREFIX}    └─ Already cached: ${alreadyCached}/${items.length}`,
    );
    console.log(
      `${LOG_PREFIX}    └─ To download: ${notCached.length} items (${this.formatBytes(totalSize)})`,
    );

    // Log each item to be downloaded
    notCached.forEach((item, index) => {
      console.log(
        `${LOG_PREFIX}    └─ [${index + 1}] ${item.name} (${this.formatBytes(item.fileSize)})`,
      );
    });

    // Queue all items with decreasing priority
    sortedItems.forEach((item, index) => {
      const priority = sortedItems.length - index; // Higher priority for earlier items
      this.queueDownload(item, priority);
    });

    console.log(`${LOG_PREFIX} 📥 Queued ${notCached.length} downloads`);
  }

  /**
   * Enforces storage limit using LRU eviction.
   * Called before downloading new content.
   *
   * @returns Number of bytes freed
   */
  async enforceStorageLimit(): Promise<number> {
    const info = await this.getStorageInfo();

    // If quota is unknown or zero, skip enforcement
    if (info.quota === 0) {
      console.log(
        `${LOG_PREFIX} ⚠️ Storage quota unknown, skipping limit enforcement`,
      );
      return 0;
    }

    const currentPercent = info.percent / 100;

    console.log(
      `${LOG_PREFIX} 📊 Storage check: ${this.formatBytes(info.usage)} / ${this.formatBytes(info.quota)} (${info.percent.toFixed(1)}%)`,
    );

    if (currentPercent < STORAGE_MAX) {
      console.log(
        `${LOG_PREFIX} ✅ Storage OK - below ${STORAGE_MAX * 100}% threshold`,
      );
      return 0; // No cleanup needed
    }

    // Calculate bytes to free to get to target
    const targetBytes = info.quota * STORAGE_TARGET;
    const bytesToFree = info.usage - targetBytes;

    console.log(
      `${LOG_PREFIX} ⚠️ Storage at ${info.percent.toFixed(1)}% - exceeds ${STORAGE_MAX * 100}% threshold`,
    );
    console.log(
      `${LOG_PREFIX} 🧹 Starting LRU cleanup: freeing ${this.formatBytes(bytesToFree)} to reach ${STORAGE_TARGET * 100}%`,
    );

    const freedBytes = await this.cleanupLRU(bytesToFree);

    // Log final state
    const afterInfo = await this.getStorageInfo();
    console.log(
      `${LOG_PREFIX} ✅ Cleanup complete: ${this.formatBytes(freedBytes)} freed`,
    );
    console.log(
      `${LOG_PREFIX}    └─ Storage now: ${this.formatBytes(afterInfo.usage)} / ${this.formatBytes(afterInfo.quota)} (${afterInfo.percent.toFixed(1)}%)`,
    );

    return freedBytes;
  }

  /**
   * Checks if there's enough space to store a file of given size.
   *
   * @param bytes - Size of file to store
   * @returns True if there's space, false otherwise
   */
  async canStore(bytes: number): Promise<boolean> {
    const info = await this.getStorageInfo();

    // If quota is unknown, assume we can store
    if (info.quota === 0) {
      return true;
    }

    const afterDownload = (info.usage + bytes) / info.quota;
    return afterDownload <= STORAGE_MAX;
  }

  /**
   * Cancels downloads for specific items.
   */
  cancelDownloads(itemIds: string[]): void {
    itemIds.forEach((id) => {
      downloadQueue.cancel(id);
      runInAction(() => {
        this.cacheStatus.delete(id);
        this.downloadProgress.delete(id);
      });
    });
  }

  /**
   * Cancels all downloads.
   */
  cancelAllDownloads(): void {
    downloadQueue.cancelAll();
    runInAction(() => {
      // Remove pending/downloading items from status
      this.cacheStatus.forEach((item, id) => {
        if (item.status === 'pending' || item.status === 'downloading') {
          this.cacheStatus.delete(id);
        }
      });
      this.downloadProgress.clear();
    });
  }

  /**
   * Registers a callback for download progress.
   */
  onDownloadProgress(callback: DownloadProgressCallback): () => void {
    return downloadQueue.onProgress(callback);
  }

  // ===========================================================================
  // DOWNLOAD QUEUE SETUP
  // ===========================================================================

  /** O `nativeMandatory` do embedded já foi decidido? (1× por sessão) */
  private nativeMandatoryDecided = false;

  /**
   * Sets up download queue callbacks.
   */
  private setupDownloadQueue(): void {
    // Progress callback
    downloadQueue.onProgress((itemId, progress) => {
      runInAction(() => {
        this.downloadProgress.set(itemId, progress);

        const cached = this.cacheStatus.get(itemId);
        if (cached && cached.status === 'pending') {
          cached.status = 'downloading';
        }
        if (cached) {
          cached.progress = progress;
        }
      });
    });

    // Completion callback
    downloadQueue.onComplete = async (item, blob) => {
      // Validate checksum
      const checksumResult = await validateChecksumDetailed(
        blob,
        item.checksum,
      );

      if (!checksumResult.valid) {
        // 2026-05-24 — Graceful degradation pro checksum mismatch.
        //
        // Descoberto via logs Chrome browser (produção) + Tizen TV: o
        // servidor está mandando checksums errados (hipótese: pipeline de
        // transcoding re-encoda mas checksum no banco é do arquivo original).
        // Browser e Tizen calculam EXATAMENTE o mesmo SHA-256, então o blob
        // está íntegro — é o `expected` que está furado.
        //
        // Política:
        //   1. Comparar blob.size com item.fileSize (size do server). Se
        //      DIFERENTES também, é truncamento real → bloqueia.
        //   2. Se só checksum mismatch + size OK → confia no blob, cacheia
        //      com warning. Operador vê via `checksumMismatchCount` no
        //      overlay e pode levantar issue pro backend.
        //   3. VITE_CHECKSUM_STRICT=true reverte ao comportamento antigo
        //      (bloqueante) pra casos onde checksum é confiável.
        const expectedPreview =
          checksumResult.expected?.substring(0, 12) ?? 'n/a';
        const calculatedPreview =
          checksumResult.calculated?.substring(0, 12) ?? 'n/a';
        const sizeMatches = blob.size === item.fileSize;
        const strictMode =
          import.meta.env.VITE_CHECKSUM_STRICT === 'true' ||
          import.meta.env.VITE_CHECKSUM_STRICT === true;

        const mismatchMsg = `checksum mismatch — expected ${expectedPreview}…, got ${calculatedPreview}… (blob ${blob.size}B, expected size ${item.fileSize}B${sizeMatches ? ' ✓' : ' ✗'})`;

        // Caso bloqueante: size também não bate (truncamento real) OU strict mode
        if (!sizeMatches || strictMode) {
          const reason = !sizeMatches
            ? 'size+checksum mismatch'
            : 'strict mode';
          logger.warn(
            `${LOG_PREFIX} ❌ Bloqueando ${item.name} (${reason}): ${mismatchMsg}`,
          );
          runInAction(() => {
            const cached = this.cacheStatus.get(item.id);
            if (cached) {
              cached.status = 'error';
              cached.error = mismatchMsg;
              cached.failedAt = Date.now();
              cached.failedCount = (cached.failedCount ?? 0) + 1;
              cached.progress = undefined;
            }
            this.downloadProgress.delete(item.id);
            this.lastCacheError = `${item.name}: ${mismatchMsg}`;
            this.lastCacheErrorAt = Date.now();
          });
          return;
        }

        // Caso permissivo: só hash não bate, size OK → cachear com telemetria
        logger.warn(
          `${LOG_PREFIX} ⚠️ Checksum mismatch (size OK, cacheando mesmo assim): ${item.name}: ${mismatchMsg}`,
        );
        runInAction(() => {
          this.checksumMismatchCount += 1;
          this.lastChecksumMismatch = `${item.name}: ${mismatchMsg}`;
        });
        // Cai pro fluxo de cache normal abaixo
      }

      // Store in cache
      // 2026-05-24 — try/catch obrigatório aqui: antes, erro do cache.store()
      // virava unhandled rejection e o item ficava "preso" em downloading
      // (nem cached, nem error). Observado em Tizen U8600F consumer: download
      // chegava a 100% mas writeBlob falhava silenciosamente, e o painel
      // mostrava "0 cached, 3 erros" sem nunca limpar.
      //
      // 2026-05-28 — Marca status='saving' ANTES de chamar cache.store(). Em
      // Tizen Consumer 9.0 com blobs grandes (>10 MB), a transaction
      // IndexedDB pode demorar até 30s pra commitar (watchdog em
      // IndexedDBCache.store linha 270). Sem esse estado intermediário, o
      // item ficava como "100% baixando" durante toda a escrita, e o operador
      // achava que estava travado (reportado por Renato, print 2026-05-28).
      // Agora o overlay mostra "salvando…" enquanto a transaction roda.
      runInAction(() => {
        const cached = this.cacheStatus.get(item.id);
        if (cached) {
          cached.status = 'saving';
          cached.progress = undefined; // o número 100% já não faz sentido aqui
        }
        this.downloadProgress.delete(item.id);
      });

      // v1.0.59: refresh storage estimate ANTES da tentativa pra ver
      // se quota está cheia (suspeito #1 de degradação cumulativa).
      await this.refreshStorageEstimate();

      const storeStartMs = Date.now();
      const sizeMB = blob.size / (1024 * 1024);

      try {
        const cache = await this.getMediaCache();
        await cache.store(item.url, blob, {
          id: item.id,
          checksum: item.checksum,
          name: item.name,
          mimeType: blob.type || undefined,
        });

        const storeDurationMs = Date.now() - storeStartMs;
        this.trackIdbOp({
          itemName: item.name,
          sizeMB: Math.round(sizeMB * 10) / 10,
          durationMs: storeDurationMs,
          result: 'success',
        });

        runInAction(() => {
          const cached = this.cacheStatus.get(item.id);
          if (cached) {
            cached.status = 'cached';
            cached.downloadedAt = new Date().toISOString();
            cached.progress = undefined;
            // Mantém `name` (já estava setado em queueDownload). Sem
            // re-atribuir aqui — preserva valor existente.
          }
          this.downloadProgress.delete(item.id);
        });
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : String(err);
        const storeDurationMs = Date.now() - storeStartMs;
        const isTimeout = errMsg.toLowerCase().includes('timeout');
        this.trackIdbOp({
          itemName: item.name,
          sizeMB: Math.round(sizeMB * 10) / 10,
          durationMs: storeDurationMs,
          result: isTimeout ? 'timeout' : 'error',
          errorMsg: errMsg,
        });
        logger.error(
          `${LOG_PREFIX} ❌ cache.store() failed for ${item.name}: ${errMsg}`,
        );
        runInAction(() => {
          const cached = this.cacheStatus.get(item.id);
          if (cached) {
            cached.status = 'error';
            cached.error = `store: ${errMsg}`;
            cached.failedAt = Date.now();
            cached.failedCount = (cached.failedCount ?? 0) + 1;
            cached.progress = undefined;
          }
          this.downloadProgress.delete(item.id);
          this.lastCacheError = `${item.name}: ${errMsg}`;
          this.lastCacheErrorAt = Date.now();
        });

        // v1.0.54 (2026-05-28) — TIZEN ONLY: forçar reconexão da IDB.
        //
        // Hipótese clínica do Renato: em Tizen Consumer 9.0, após
        // transaction.abort() (disparado pelo watchdog 120s), a conexão
        // IDB fica com estado residual. Sintoma observado em campo: o
        // mesmo item que falhou uma vez vira "permanentemente
        // não-cacheável" — sempre aparece em "💾 salvando..." em loop,
        // sempre dá timeout 120s.
        //
        // forceReconnect fecha e reabre a IDBDatabase. Próxima
        // cache.store() vai usar uma conexão fresca, eliminando estado
        // residual da transaction abortada.
        //
        // Isolando essa variável: SE com esse fix o item passa a cachear
        // no retry, confirmamos que era estado residual do IDB. Se NÃO
        // resolver, o "lixo" está em outro lugar e investigamos a próxima
        // hipótese.
        //
        // Restrito a Tizen — outras plataformas não têm esse bug.
        if (detectPlatform() === 'tizen') {
          try {
            const cache = await this.getMediaCache();
            if (cache.forceReconnect) {
              await cache.forceReconnect();
              runInAction(() => {
                this.forceReconnectCount++;
              });
              logger.info(
                `${LOG_PREFIX} 🔄 IDB reconnected após falha de cache.store em Tizen (total: ${this.forceReconnectCount})`,
              );
            }
          } catch (reconnErr) {
            logger.error(
              `${LOG_PREFIX} ⚠️ forceReconnect falhou (não fatal):`,
              reconnErr,
            );
          }
        }
      }
    };

    // Error callback
    //
    // After MAX_RETRIES exhausted in DownloadQueue, we mark the item as 'error'
    // but stamp `failedAt` so that `queueDownload` can re-enqueue it once
    // ERROR_RETRY_COOLDOWN_MS has elapsed. This is the recovery path for
    // transient failures (e.g., R2 CORS misconfig, server downtime) that get
    // resolved without restarting the player.
    downloadQueue.onError = (item, error) => {
      runInAction(() => {
        const cached = this.cacheStatus.get(item.id);
        if (cached) {
          cached.status = 'error';
          cached.error = error;
          cached.failedAt = Date.now();
          cached.failedCount = (cached.failedCount ?? 0) + 1;
          cached.progress = undefined;
          logger.warn(
            `${LOG_PREFIX} ❌ Download failed for ${item.name} (count=${cached.failedCount}); will retry after ${ERROR_RETRY_COOLDOWN_MS / 1000}s cooldown on next queueDownload call.`,
          );
        }
        this.downloadProgress.delete(item.id);
        // 2026-05-24 — também guarda erro de download pra debug overlay.
        this.lastCacheError = `${item.name}: download — ${error}`;
        this.lastCacheErrorAt = Date.now();
      });
    };

    // Native download path (Tizen, opt-in). When enabled, DownloadQueue
    // transfers via tizen.download (rede→disco off-thread) instead of
    // fetch→blob→writeBlob — elimina o freeze de ~20s do writeBlob síncrono
    // em vídeos grandes (a "carroça" do cold-download). Mantém toda a
    // orquestração da fila (concorrência/retry/priority).
    if (this.shouldUseNativeDownload()) {
      downloadQueue.nativeDownloader = async (item, onProgress, signal) => {
        const cache = await this.getMediaCache();
        // Só DEPOIS do cache resolvido dá pra saber se o host embedded tem a
        // bridge nativa — e é aqui, antes de qualquer tentativa nativa, que o
        // `nativeMandatory` é fixado. Vide applyEmbeddedNativeMandatory.
        this.applyEmbeddedNativeMandatory(cache);
        if (typeof cache.storeViaNativeDownload !== 'function') {
          throw new Error(
            'storeViaNativeDownload não suportado por este cache',
          );
        }
        await cache.storeViaNativeDownload(
          item.url,
          {
            id: item.id,
            checksum: item.checksum,
            name: item.name,
            expectedSize: item.fileSize,
          },
          onProgress,
          signal,
        );
      };
      downloadQueue.onNativeComplete = (item) => {
        runInAction(() => {
          const cached = this.cacheStatus.get(item.id);
          if (cached) {
            cached.status = 'cached';
            cached.downloadedAt = new Date().toISOString();
            cached.progress = undefined;
          }
          this.downloadProgress.delete(item.id);
        });
      };
      logger.info(
        `${LOG_PREFIX} 🚀 Native download ligado (${detectPlatform() === 'embedded' ? 'EffluxHost' : 'tizen.download'}) — bypass do fetch→blob no renderer`,
      );
    }
  }

  /**
   * EMBEDDED: fixa o `nativeMandatory` da fila pela CAPACIDADE REAL do host —
   * não pela plataforma. Decisão única na sessão.
   *
   * `nativeMandatory` desliga o circuit-breaker: falha nativa NUNCA cai no
   * blob/fetch-no-renderer (a "bomba-relógio" que vaza VA do renderer 32-bit →
   * renderer-kill). Esse guard só pode valer quando o host SABE baixar nativo.
   *
   * Chavear por `detectPlatform() === 'embedded'` quebrava o AAR ANTIGO — o que
   * injeta `EffluxHost` pro canal de comandos/eventos mas SEM os métodos de mídia
   * (`cacheMedia`/`isMediaCached`). Nesse host o `cacheFactory` cai — corretamente
   * — no MediaCache do browser, que não tem `storeViaNativeDownload`: toda
   * tentativa nativa falhava, o breaker ficava travado fechado e o device nunca
   * cacheava NADA, streamando remoto pra sempre (pior perfil possível de banda, e
   * quebra do offline-first).
   *
   * O `cache` já resolvido é a fonte certa da decisão: o `cacheFactory` só devolve
   * o `EmbeddedMediaCache` depois de ESPERAR a bridge (`waitForNativeMediaBridge`),
   * então aqui a resposta é definitiva E casa com o que o downloader realmente
   * consegue fazer — sem corrida com o boot, mesmo com a bridge aterrissando tarde.
   * Como este é o ÚNICO caminho nativo, a decisão acontece sempre ANTES de a fila
   * poder contabilizar qualquer falha nativa.
   *
   * Tizen segue com `nativeMandatory=false` (TVs com `tizen.download` presente-mas-
   * quebrado precisam do fallback blob); android/browser não chegam aqui.
   */
  private applyEmbeddedNativeMandatory(cache: IMediaCache): void {
    if (this.nativeMandatoryDecided) return;
    if (detectPlatform() !== 'embedded') return;

    this.nativeMandatoryDecided = true;
    const hostCanDownload = typeof cache.storeViaNativeDownload === 'function';
    downloadQueue.nativeMandatory = hostCanDownload;
    logger.info(
      hostCanDownload
        ? `${LOG_PREFIX} 🔒 Embedded: bridge nativa presente → download nativo MANDATORY (a fila nunca cai no blob)`
        : `${LOG_PREFIX} ⚠️ Embedded SEM bridge nativa de mídia (AAR antigo) → circuit-breaker LIGADO: cai no blob e volta a cachear`,
    );
  }

  /**
   * True quando o download nativo (tizen.download) deve ser usado.
   *
   * Default ON em Tizen quando a API está presente (exige privilege
   * `http://tizen.org/privilege/download` no config.xml). Elimina o freeze de
   * ~20s do writeBlob síncrono. Kill-switch de rollback:
   * `VITE_TIZEN_NATIVE_DOWNLOAD=false`. TVs sem a API caem no writeBlob via
   * este check; TVs com a API presente-mas-quebrada caem via circuit-breaker
   * na DownloadQueue (NATIVE_BREAKER_THRESHOLD).
   */
  private shouldUseNativeDownload(): boolean {
    if (import.meta.env.VITE_TIZEN_NATIVE_DOWNLOAD === 'false') return false; // kill switch
    const platform = detectPlatform();
    // Embedded (AAR): baixa a mídia NATIVO pela bridge do host (rede→disco fora
    // do renderer) — cura do leak de VA 32-bit. Mesmo mecanismo do Tizen, host
    // diferente (EffluxHost.cacheMedia). Aqui o caminho nativo é sempre ARMADO;
    // quem depende da bridge de verdade é o `nativeMandatory` (vide
    // applyEmbeddedNativeMandatory), decidido lazy com o cache já resolvido — num
    // AAR antigo ele fica false e o circuit-breaker devolve o fallback blob.
    if (platform === 'embedded') {
      // ⚠️ NÃO gatear em `EffluxHost` aqui: `setupDownloadQueue` roda no CONSTRUCTOR
      // (import do módulo), ANTES do `addJavascriptInterface(EffluxHost)` estar
      // disponível no JS. Gatear aqui PERDIA a corrida → a DownloadQueue ficava
      // presa no caminho blob/fetch-no-renderer PARA SEMPRE (bug: download não-nativo
      // + playback REMOTO enquanto baixa → vaza VA do renderer 32-bit → renderer-kill).
      // `__effluxEmbedded` (injetado no `onPageStarted`, antes do bundle) já garante
      // que é embedded. A checagem real do host é feita LAZY em
      // `EmbeddedMediaCache.storeViaNativeDownload` (que ESPERA o EffluxHost aparecer).
      // O `cacheFactory` segue a MESMA regra: espera a bridge antes de memoizar a
      // escolha do cache (`waitForNativeMediaBridge`), então um host atrasado não
      // prende mais o embedded no MediaCache do browser. Só AAR realmente antigo
      // (EffluxHost presente SEM os métodos de mídia) cai no browser cache.
      return true;
    }
    if (platform !== 'tizen') return false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tz = (window as any).tizen;
    return (
      !!tz &&
      !!tz.download &&
      typeof tz.download.start === 'function' &&
      typeof tz.DownloadRequest === 'function'
    );
  }

  // ===========================================================================
  // UTILITIES
  // ===========================================================================

  /**
   * Resets the store.
   */
  reset(): void {
    downloadQueue.cancelAll();
    this.cacheStatus.clear();
    this.downloadProgress.clear();
    this.isInitialized = false;
  }

  /**
   * Gets storage info.
   */
  async getStorageInfo(): Promise<{
    usage: number;
    quota: number;
    percent: number;
  }> {
    const cache = await this.getMediaCache();
    const info = await cache.getStorageInfo();
    const percent = info.quota > 0 ? (info.usage / info.quota) * 100 : 0;
    return { ...info, percent };
  }

  /**
   * Formats bytes to human readable string.
   */
  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }

  /**
   * Debug: logs current state.
   */
  debugState(): void {
    console.group(`${LOG_PREFIX} Debug State`);
    console.log('Is initialized:', this.isInitialized);
    console.log('Cached items:', this.cachedItems.length);
    console.log('Pending downloads:', this.pendingDownloads);
    console.log('Downloading:', this.downloadingItems.length);
    console.log('Errors:', this.errorItems.length);
    console.log('Total cache size:', this.formatBytes(this.totalCacheSize));
    console.log('Queue length:', downloadQueue.getQueueLength());
    console.log('Active downloads:', downloadQueue.getActiveDownloads());
    console.groupEnd();
  }
}

export default MediaStore;
